import type { Practice, Routine } from '../types';
import type { AudioBackend } from './engine';
import { SessionEngine } from './engine';
import { createWebAudioBackend } from './audioBackend';
import { addHistory, markSeen, saveSettings } from './storage';
import { settingsFor } from './routines';

export type Phase = 'playing' | 'between' | 'done';

export interface Run {
  routine: Routine | null;
  queue: Practice[];
  index: number;
  engine: SessionEngine;
  phase: Phase;
  /** Practices finished so far, for the closing summary. */
  completed: { practiceId: string; seconds: number }[];
}

let run: Run | null = null;
let backend: AudioBackend | null = null;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach(fn => fn());

export const subscribeRun = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const getRun = () => run;

/** One backend (one AudioContext, one music element) for the whole app session, so chained practices need no new tap. */
const sharedBackend = () => (backend ??= createWebAudioBackend());

function makeEngine(practice: Practice, chained: boolean, routine: Routine | null): SessionEngine {
  const engine = new SessionEngine({
    practice,
    settings: settingsFor(routine, practice),
    backend: sharedBackend(),
    holdAudio: chained,
    onSettingsChange: s => saveSettings(practice.id, s),
    onComplete: ({ seconds }) => {
      addHistory({ practiceId: practice.id, endedAt: new Date().toISOString(), seconds, completed: true });
      if (!run) return;
      run = {
        ...run,
        completed: [...run.completed, { practiceId: practice.id, seconds }],
        phase: run.index < run.queue.length - 1 ? 'between' : 'done',
      };
      if (run.phase === 'done') backend?.keepAlive(false);
      emit();
    },
  });
  if (import.meta.env.DEV) (window as unknown as { __session?: SessionEngine }).__session = engine; // dev-only test hook
  return engine;
}

/** Start a run of one or more practices. Call synchronously from the Begin tap so iOS allows audio. */
export function beginRun(queue: Practice[], routine: Routine | null = null): SessionEngine {
  endRun();
  markSeen();
  const engine = makeEngine(queue[0], queue.length > 1, routine);
  run = { routine, queue, index: 0, engine, phase: 'playing', completed: [] };
  engine.begin();
  emit();
  return engine;
}

/** Play the next practice. Works without a tap because the audio element is already unlocked. */
export function advanceRun() {
  if (!run || run.index >= run.queue.length - 1) {
    if (run) {
      run = { ...run, phase: 'done' };
      backend?.keepAlive(false);
      emit();
    }
    return;
  }
  run.engine.destroy();
  const index = run.index + 1;
  const engine = makeEngine(run.queue[index], true, run.routine);
  run = { ...run, index, engine, phase: 'playing' };
  engine.begin();
  emit();
}

/** Skip the rest of the current practice. Time already spent is kept in history. */
export function skipCurrent() {
  if (!run) return;
  const snap = run.engine.getSnapshot();
  if (snap.status !== 'complete' && snap.position >= 60) {
    addHistory({ practiceId: run.queue[run.index].id, endedAt: new Date().toISOString(), seconds: Math.round(snap.position), completed: false });
  }
  advanceRun();
}

/** Leave the run entirely. A practice ended early after a minute is recorded as not completed. */
export function endRun() {
  if (!run) return;
  const { engine, queue, index } = run;
  const snap = engine.getSnapshot();
  if (snap.status !== 'complete' && snap.position >= 60) {
    addHistory({ practiceId: queue[index].id, endedAt: new Date().toISOString(), seconds: Math.round(snap.position), completed: false });
  }
  engine.destroy();
  backend?.keepAlive(false);
  run = null;
  emit();
}
